import type { Dispatch, SetStateAction } from 'react';
import { useEffect, useState } from 'react';

type UseNextToggler = (isOpen: boolean) => [boolean, Dispatch<SetStateAction<boolean>>];

export const useNextToggler: UseNextToggler = (isOpen: boolean) => {
  const [isNextDisabled, setIsNextDisabled] = useState(true);

  useEffect(() => {
    if (!isOpen) {
      setIsNextDisabled(true);
    }
  }, [isOpen]);

  return [isNextDisabled, setIsNextDisabled];
};

interface UseStepFormResponse<T> {
  activeStep: T;
  steps: T[];
  setSteps: (newSteps: T[]) => void;
  navigateToPreviousStep: () => boolean;
  navigateToNextStep: (currentSteps?: T[]) => boolean;
  resetActiveStep: () => void;
}

type UseStepForm = <T>(subscriptionSteps: T[]) => UseStepFormResponse<T>;

export const useStepForm: UseStepForm = <T>(subscriptionSteps: T[]) => {
  const [steps, setSteps] = useState<T[]>([]);
  const [activeStep, setActiveStep] = useState<T>(null);

  const navigateToPreviousStep = () => {
    const newActiveStepIndex = steps.indexOf(activeStep) - 1;
    const wasFirstStep = newActiveStepIndex < 0;

    if (!wasFirstStep) {
      setActiveStep(steps[newActiveStepIndex]);
    }

    return wasFirstStep;
  };

  const navigateToNextStep = (currentSteps = steps) => {
    const newActiveStepIndex = currentSteps.indexOf(activeStep) + 1;
    const wasLastStep = newActiveStepIndex === currentSteps.length;

    if (!wasLastStep) {
      setActiveStep(currentSteps[newActiveStepIndex]);
    }

    return wasLastStep;
  };

  const resetActiveStep = () => {
    setActiveStep(null);
  };

  // Received new steps from a subscription.
  // We need to initialize process or free up memory from previous process.
  useEffect(() => {
    setSteps(subscriptionSteps || []);
  }, [subscriptionSteps]);

  // Reclamation process was initialized or additional steps were added.
  useEffect(() => {
    setActiveStep(prevActiveStep => {
      if (!steps.length) {
        return null;
      }

      return prevActiveStep ? prevActiveStep : steps[0];
    });
  }, [steps]);

  return {
    activeStep,
    steps,
    setSteps,
    navigateToPreviousStep,
    navigateToNextStep,
    resetActiveStep,
  };
};
